import { Dialog } from "@headlessui/react"
import { KTitleSpan, KSpan, KButton, KDropdown, KInput } from "kahuna-base-react-components"
import React, { useEffect, useState } from "react"
import { lang } from "../../../../../constants/languages"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { User, UserExpense, UserExpenseForm } from "../../../../../types"
import adminUserExpenseValidator from "./validators/adminUserExpenseValidator"
import { USER_ROLE } from "../../../../../constants"

export const TaxTypeOptions = [
  { value: 1, label: "KDV" },
  { value: 2, label: "Stopaj" },
  { value: 3, label: "No Tax" }
]
export const PaymentPeriodTypeOptions = [
  { value: 1, label: "Quarterly" },
  { value: 2, label: "Monthly" }
]
export const CurrencyTypeOptions = [
  { value: 1, label: "TRY" },
  { value: 2, label: "USD" },
  { value: 3, label: "EUR" },
  { value: 4, label: "GBP" }
]

const AdminUserExpenseModal = ({
  open,
  onClose,
  selectedUserExpense,
  onUserExpenseFormSubmit,
  onApplyPreviousMonths,
  user,
  loggedInUser,
  isModal
}: {
  open: boolean
  onClose: () => void
  selectedUserExpense: UserExpense
  onUserExpenseFormSubmit: (userExpenseForm: UserExpenseForm) => Promise<void>
  onApplyPreviousMonths?: (userExpenseId?: number) => Promise<void>
  user: User
  loggedInUser?: User
  isModal: boolean
}) => {
  const [userExpense, setUserExpense] = useState<UserExpense>(selectedUserExpense)
  const [initialUserExpense, setInitialUserExpense] = useState<UserExpense>(selectedUserExpense)

  useEffect(() => {
    setUserExpense(selectedUserExpense)
    setInitialUserExpense(selectedUserExpense)
  }, [selectedUserExpense])

  const createUserExpenseFormFromUserExpense = (userExpense: UserExpense) => {
    const userExpenseForm: UserExpenseForm = {
      id: userExpense?.id,
      adminFee: userExpense?.adminFee || 0,
      distributionFee: userExpense?.distributionFee || 0,
      user: userExpense?.user,
      taxType: userExpense?.taxType || 1,
      preferredCurrency: userExpense?.preferredCurrency || 1,
      paymentPeriod: userExpense?.paymentPeriod || 1,
      publishingKahunaShare: userExpense?.publishingKahunaShare || 0,
      paymentThreshold: userExpense?.paymentThreshold || 0
    }

    return userExpenseForm
  }

  const renderMain = () => {
    return (
      <div className="p-6 w-[400px] rounded-[10px] bg-[#fff] elev-b-sm">
        <div className="flex flex-col gap-6">
          {isModal && (
            <div className="flex flex-col gap-6 items-center justify-center">
              <span
                className="w-[72px] aspect-square rounded-full flex justify-center items-center"
                style={{
                  border: "1px solid #F3F3F3"
                }}
              >
                <img className="w-8 h-8" src="/admin_icons/user-gray.svg" />
              </span>
              <KTitleSpan text={"User Expense"} fontSize={20} lineHeight="28px" color="#000" />
            </div>
          )}
          {isModal && (
            <div className="flex flex-row items-center gap-1">
              <KSpan text="User:" fontWeight={500} color="#000" />
              <KSpan text={`${user?.firstName} ${user?.lastName}`} color="#000" />
            </div>
          )}
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2">
              <div className="flex flex-col w-1/2 gap-0.5 items-start">
                <KSpan text="Admin Fee" />
                <KInput
                  dataTestId="admin-fee-input"
                  type="number"
                  value={userExpense?.adminFee?.toString() || ""}
                  onChange={(text) => {
                    setUserExpense({ ...userExpense, adminFee: Number(text) })
                  }}
                />
              </div>
              <div className="flex flex-col w-1/2 gap-0.5 items-start">
                <KSpan text="Distribution Fee" />
                <KInput
                  dataTestId="distribution-fee-input"
                  type="number"
                  value={userExpense?.distributionFee?.toString() || ""}
                  onChange={(text) => {
                    setUserExpense({ ...userExpense, distributionFee: Number(text) })
                  }}
                />
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <div className="flex flex-col w-1/2 gap-0.5 items-start">
                <KSpan text="Tax Type" />
                <KDropdown
                  dataTestId="tax-type-dropdown"
                  options={TaxTypeOptions}
                  selected={TaxTypeOptions.find((option) => option.value === userExpense?.taxType)}
                  onSelect={(selected) => {
                    setUserExpense({ ...userExpense, taxType: (selected as KSelectOption)?.value })
                  }}
                  rightIcon="/analytics_icons/caret-down-new.svg"
                  enableIndicator
                />
              </div>
              <div className="flex flex-col w-1/2 gap-0.5 items-start">
                <KSpan text="Preferred Currency" />
                <KDropdown
                  dataTestId="preferred-currency-dropdown"
                  options={CurrencyTypeOptions}
                  selected={CurrencyTypeOptions.find((option) => option.value === userExpense?.preferredCurrency)}
                  onSelect={(selected) => {
                    setUserExpense({ ...userExpense, preferredCurrency: (selected as KSelectOption)?.value })
                  }}
                  rightIcon="/analytics_icons/caret-down-new.svg"
                  enableIndicator
                />
              </div>
            </div>
            <div className="flex flex-row ">
              <div className="flex flex-col w-full gap-0.5 items-start">
                <KSpan text="Payment Period Type" />
                <KDropdown
                  dataTestId="payment-period-type-dropdown"
                  options={PaymentPeriodTypeOptions}
                  selected={PaymentPeriodTypeOptions.find((option) => option.value === userExpense?.paymentPeriod)}
                  onSelect={(selected) => {
                    setUserExpense({ ...userExpense, paymentPeriod: (selected as KSelectOption)?.value })
                  }}
                  rightIcon="/analytics_icons/caret-down-new.svg"
                  enableIndicator
                />
              </div>
            </div>
            <div className="flex flex-row gap-2">
              <div className="flex flex-col w-1/2 gap-0.5 items-start">
                <KSpan text="Publishing Kahuna Share" />
                <KInput
                  dataTestId="publishing-kahuna-share-input"
                  type="number"
                  value={userExpense?.publishingKahunaShare?.toString() || ""}
                  onChange={(text) => {
                    setUserExpense({ ...userExpense, publishingKahunaShare: Number(text) })
                  }}
                />
              </div>
              <div className="flex flex-col w-1/2 gap-0.5 items-start">
                <KSpan text="Payment Threshold" />
                <KInput
                  dataTestId="payment-threshold-input"
                  type="number"
                  value={userExpense?.paymentThreshold?.toString() || ""}
                  onChange={(text) => {
                    setUserExpense({ ...userExpense, paymentThreshold: Number(text) })
                  }}
                />
              </div>
            </div>
          </div>
          <div className="flex gap-3 justify-center items-center">
            <KButton
              dataTestId="cancel-user-expense-button"
              text={lang.common.cancel}
              onClick={() => {
                onClose()
              }}
              background="#fff"
              shadowDisabled={true}
            />
            <KButton
              dataTestId="confirm-user-expense-button"
              text={lang.common.save}
              textColor="white"
              disabled={JSON.stringify(userExpense) === JSON.stringify(initialUserExpense)}
              background="black"
              onClick={() => {
                const errors = adminUserExpenseValidator(userExpense)
                if (Object.keys(errors).length === 0) {
                  onUserExpenseFormSubmit(createUserExpenseFormFromUserExpense(userExpense))
                  onClose()
                }
              }}
            />
          </div>
          {onApplyPreviousMonths && loggedInUser?.roleId === USER_ROLE.SuperAdmin && (
            <div className="mt-8">
              <KButton
                dataTestId="confirm-user-apply-all-button"
                text={"Apply to previous months!"}
                textColor="white"
                disabled={JSON.stringify(userExpense) !== JSON.stringify(initialUserExpense)}
                background="red"
                onClick={() => onApplyPreviousMonths(userExpense.id)}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  return isModal ? (
    <Dialog
      open={open}
      onClose={() => {
        onClose()
      }}
    >
      <div className="fixed w-screen h-screen top-0 left-0 z-250 flex items-center justify-center bg-[#0000004d] ">
        {renderMain()}
      </div>
    </Dialog>
  ) : (
    renderMain()
  )
}

export default AdminUserExpenseModal
